import React, { useState } from 'react';
import {
  Box, Typography, Stack, Paper, Grid, Button, TextField, Chip,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  Select, MenuItem, FormControl, InputLabel, FormControlLabel, Switch,
  Table, TableCell, TableContainer, TableHead, TableRow,
  Alert, Snackbar, CircularProgress, Tooltip, Divider,
} from '@mui/material';
import TableBody from '../components/PagedTableBody'; // pages rows beyond 10
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import QuizIcon from '@mui/icons-material/Quiz';
import { useQuery, useMutation } from '@apollo/client';
import {
  ALL_QUESTION_TEMPLATES_QUERY,
  QUESTION_CATEGORIES_QUERY,
  CREATE_QUESTION_TEMPLATE_MUTATION,
  UPDATE_QUESTION_TEMPLATE_MUTATION,
  DELETE_QUESTION_TEMPLATE_MUTATION,
  REORDER_QUESTION_TEMPLATES_MUTATION,
} from '../graphql/queries';

const QUESTION_TYPES = [
  { value: 'text', label: 'Text Input' },
  { value: 'select', label: 'Single Select (Radio)' },
  { value: 'multiselect', label: 'Multi Select (Checkboxes)' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'radio', label: 'Single Select (Radio buttons)' },
  { value: 'checkbox', label: 'Multi Select (Checkbox list)' },
  { value: 'textarea', label: 'Long Text' },
];
// Types answered by picking from options
const isChoice = (type) => ['select', 'multiselect', 'radio', 'checkbox'].includes(type);

const SERVICE_CATEGORIES = [
  'venue', 'catering', 'decoration', 'photography', 'music_entertainment',
  'transport', 'rituals', 'beauty', 'invitation', 'accommodation', 'gifts', 'honeymoon',
];

const emptyForm = () => ({
  serviceCategory: '',
  questionKey: '',
  question: '',
  type: 'text',
  options: [],
  required: true,
  sortOrder: 0,
  dependsOn: '',
  dependsOnValue: '',
  minBudget: '',
  maxBudget: '',
  budgetAnswer: '',
  optionBudgets: {}, // option -> minimum service budget
});

const BUDGET_ANSWER_OPTIONS = [
  { value: '', label: 'No — the family answers it' },
  { value: 'total', label: 'Yes — from the service budget' },
  { value: 'per_guest', label: 'Yes — from the service budget per guest' },
];
const rupees = (v) => `₹${Number(v).toLocaleString('en-IN')}`;
const budgetSummary = (q) => [
  q.budgetAnswer && (q.budgetAnswer === 'per_guest' ? 'answered from budget per guest' : 'answered from budget'),
  q.minBudget != null && `from ${rupees(q.minBudget)}`,
  q.maxBudget != null && `up to ${rupees(q.maxBudget)}`,
  (q.optionBudgets || []).length && `${q.optionBudgets.length} option${q.optionBudgets.length === 1 ? '' : 's'} need a higher budget`,
].filter(Boolean).join(' · ');

export default function AdminQuestionsPage() {
  const [selectedCategory, setSelectedCategory] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [optionInput, setOptionInput] = useState('');
  const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });

  const { data: catData } = useQuery(QUESTION_CATEGORIES_QUERY);
  const { data, loading, refetch } = useQuery(ALL_QUESTION_TEMPLATES_QUERY, {
    variables: { category: selectedCategory || null },
    fetchPolicy: 'cache-and-network',
  });

  const [createQuestion, { loading: creating }] = useMutation(CREATE_QUESTION_TEMPLATE_MUTATION, {
    onCompleted: () => { refetch(); closeDialog(); showSnack('Question created!'); },
    onError: e => showSnack(e.message, 'error'),
  });

  const [updateQuestion] = useMutation(UPDATE_QUESTION_TEMPLATE_MUTATION, {
    onCompleted: () => { refetch(); closeDialog(); showSnack('Question updated!'); },
    onError: e => showSnack(e.message, 'error'),
  });

  const [deleteQuestion] = useMutation(DELETE_QUESTION_TEMPLATE_MUTATION, {
    onCompleted: () => { refetch(); showSnack('Question deleted.'); },
    onError: e => showSnack(e.message, 'error'),
  });

  const [reorderQuestions] = useMutation(REORDER_QUESTION_TEMPLATES_MUTATION, {
    onCompleted: () => refetch(),
    onError: e => showSnack(e.message, 'error'),
  });

  const questions = data?.allQuestionTemplates || [];
  const categories = catData?.questionCategories || SERVICE_CATEGORIES;

  const showSnack = (msg, severity = 'success') => setSnack({ open: true, msg, severity });

  const openCreate = () => {
    setEditTarget(null);
    setForm({ ...emptyForm(), serviceCategory: selectedCategory });
    setDialogOpen(true);
  };

  const openEdit = (q) => {
    setEditTarget(q);
    setForm({
      serviceCategory: q.category,
      questionKey: q.questionKey,
      question: q.question,
      type: q.type,
      options: q.options || [],
      required: q.required,
      sortOrder: q.sortOrder || 0,
      dependsOn: q.dependsOn || '',
      dependsOnValue: q.dependsOnValue || '',
      minBudget: q.minBudget ?? '',
      maxBudget: q.maxBudget ?? '',
      budgetAnswer: q.budgetAnswer || '',
      optionBudgets: Object.fromEntries((q.optionBudgets || []).map(o => [o.option, o.minBudget])),
    });
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditTarget(null);
    setForm(emptyForm());
    setOptionInput('');
  };

  const handleSave = () => {
    if (!form.serviceCategory || !form.questionKey || !form.question) {
      showSnack('Category, Key, and Question text are required', 'warning');
      return;
    }
    const input = {
      serviceCategory: form.serviceCategory,
      questionKey: form.questionKey,
      question: form.question,
      type: form.type,
      options: form.options.length > 0 ? form.options : null,
      required: form.required,
      sortOrder: form.sortOrder || 0,
      dependsOn: form.dependsOn || null,
      dependsOnValue: form.dependsOnValue || null,
      minBudget: form.minBudget === '' ? null : Number(form.minBudget),
      maxBudget: form.maxBudget === '' ? null : Number(form.maxBudget),
      budgetAnswer: form.budgetAnswer || null,
      optionBudgets: Object.entries(form.optionBudgets)
        .filter(([o, v]) => form.options.includes(o) && Number(v) > 0)
        .map(([option, v]) => ({ option, minBudget: Number(v) })),
    };
    if (editTarget) {
      updateQuestion({ variables: { input: { id: editTarget.id, ...input } } });
    } else {
      createQuestion({ variables: { input } });
    }
  };

  const handleDelete = (id) => {
    if (window.confirm('Delete this question? This cannot be undone.')) {
      deleteQuestion({ variables: { id } });
    }
  };

  const addOption = () => {
    if (optionInput.trim()) {
      setForm(f => ({ ...f, options: [...f.options, optionInput.trim()] }));
      setOptionInput('');
    }
  };

  const removeOption = (idx) => {
    setForm(f => ({ ...f, options: f.options.filter((_, i) => i !== idx) }));
  };

  const moveQuestion = (idx, direction) => {
    const catQuestions = questions.filter(q => q.category === (selectedCategory || q.category));
    if (selectedCategory) {
      const ids = catQuestions.map(q => q.id);
      const newIds = [...ids];
      const targetIdx = idx + direction;
      if (targetIdx < 0 || targetIdx >= newIds.length) return;
      [newIds[idx], newIds[targetIdx]] = [newIds[targetIdx], newIds[idx]];
      reorderQuestions({ variables: { category: selectedCategory, orderedIds: newIds } });
    }
  };

  // Group by category for display
  const grouped = {};
  questions.forEach(q => {
    if (!grouped[q.category]) grouped[q.category] = [];
    grouped[q.category].push(q);
  });

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Service Questions Manager</Typography>
          <Typography variant="body2" color="text.secondary">
            Add, edit, delete, and reorder questions shown in the Service Wizard for each category
          </Typography>
        </Box>
        <Button startIcon={<AddIcon />} variant="contained" onClick={openCreate}>
          Add Question
        </Button>
      </Stack>

      {/* Category filter */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 3 }}>
        <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
          <Typography variant="subtitle2" fontWeight={600} mr={1}>Filter by category:</Typography>
          <Chip
            label="All"
            onClick={() => setSelectedCategory('')}
            color={!selectedCategory ? 'primary' : 'default'}
            variant={!selectedCategory ? 'filled' : 'outlined'}
          />
          {categories.map(cat => (
            <Chip
              key={cat}
              label={cat.replace('_', ' ')}
              onClick={() => setSelectedCategory(cat)}
              color={selectedCategory === cat ? 'primary' : 'default'}
              variant={selectedCategory === cat ? 'filled' : 'outlined'}
              sx={{ textTransform: 'capitalize' }}
            />
          ))}
        </Stack>
      </Paper>

      {loading && <CircularProgress sx={{ display: 'block', mx: 'auto', my: 4 }} />}

      {/* Questions table */}
      {!loading && Object.keys(grouped).length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3, border: '2px dashed #e0e0e0' }}>
          <QuizIcon sx={{ fontSize: 48, color: '#bdbdbd', mb: 1 }} />
          <Typography color="text.secondary">No questions found. Click "Add Question" to create one.</Typography>
        </Paper>
      )}

      {Object.entries(grouped).map(([cat, catQuestions]) => (
        <Paper key={cat} sx={{ mb: 3, borderRadius: 3, overflow: 'hidden' }}>
          <Box sx={{ px: 3, py: 1.5, bgcolor: '#f3e5f5' }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ textTransform: 'capitalize' }}>
              {cat.replace('_', ' ')} ({catQuestions.length} questions)
            </Typography>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width={50}>#</TableCell>
                  <TableCell>Question</TableCell>
                  <TableCell width={100}>Type</TableCell>
                  <TableCell width={100}>Required</TableCell>
                  <TableCell width={80}>Active</TableCell>
                  <TableCell width={180}>Options</TableCell>
                  <TableCell width={140} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {catQuestions.map((q, idx) => (
                  <TableRow key={q.id} hover>
                    <TableCell>{q.sortOrder || idx + 1}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>{q.question}</Typography>
                      <Typography variant="caption" color="text.secondary" display="block">Key: {q.questionKey}</Typography>
                      {budgetSummary(q) && <Typography variant="caption" color="primary.main">Budget: {budgetSummary(q)}</Typography>}
                    </TableCell>
                    <TableCell>
                      <Chip label={q.type} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      {q.required ? <Chip label="Yes" size="small" color="error" variant="outlined" /> : <Chip label="No" size="small" />}
                    </TableCell>
                    <TableCell>
                      {q.isActive !== false ? <Chip label="Active" size="small" color="success" variant="outlined" /> : <Chip label="Inactive" size="small" color="default" />}
                    </TableCell>
                    <TableCell>
                      {(q.options || []).length > 0 ? (
                        <Tooltip title={(q.options || []).join(', ')}>
                          <Chip label={`${q.options.length} options`} size="small" />
                        </Tooltip>
                      ) : '—'}
                    </TableCell>
                    <TableCell align="right">
                      {selectedCategory && (
                        <>
                          <IconButton size="small" disabled={idx === 0} onClick={() => moveQuestion(idx, -1)}>
                            <ArrowUpwardIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" disabled={idx === catQuestions.length - 1} onClick={() => moveQuestion(idx, 1)}>
                            <ArrowDownwardIcon fontSize="small" />
                          </IconButton>
                        </>
                      )}
                      <IconButton size="small" onClick={() => openEdit(q)}><EditIcon fontSize="small" /></IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDelete(q.id)}><DeleteIcon fontSize="small" /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      ))}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editTarget ? 'Edit Question' : 'Add New Question'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} mt={1}>
            <FormControl fullWidth required>
              <InputLabel>Service Category</InputLabel>
              <Select value={form.serviceCategory} label="Service Category" onChange={e => setForm(f => ({ ...f, serviceCategory: e.target.value }))}>
                {categories.map(cat => (
                  <MenuItem key={cat} value={cat} sx={{ textTransform: 'capitalize' }}>{cat.replace(/_/g, ' ')}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Question Key (unique identifier)"
              value={form.questionKey}
              onChange={e => setForm(f => ({ ...f, questionKey: e.target.value }))}
              required
              helperText="e.g. venue_type, catering_budget"
              fullWidth
            />
            <TextField
              label="Question Text"
              value={form.question}
              onChange={e => setForm(f => ({ ...f, question: e.target.value }))}
              required
              multiline
              rows={2}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Question Type</InputLabel>
              <Select value={form.type} label="Question Type" onChange={e => setForm(f => ({ ...f, type: e.target.value }))}>
                {QUESTION_TYPES.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
              </Select>
            </FormControl>

            {isChoice(form.type) && (
              <Box>
                <Typography variant="subtitle2" fontWeight={600} mb={1}>Answer Options</Typography>
                <Stack direction="row" spacing={1} mb={1}>
                  <TextField
                    size="small"
                    placeholder="Add an option..."
                    value={optionInput}
                    onChange={e => setOptionInput(e.target.value)}
                    onKeyPress={e => e.key === 'Enter' && (e.preventDefault(), addOption())}
                    fullWidth
                  />
                  <Button variant="outlined" size="small" onClick={addOption}>Add</Button>
                </Stack>
                <Stack direction="row" spacing={0.5} flexWrap="wrap">
                  {form.options.map((opt, i) => (
                    <Chip key={i} label={opt} onDelete={() => removeOption(i)} size="small" sx={{ mb: 0.5 }} />
                  ))}
                </Stack>
                {form.options.length === 0 && (
                  <Typography variant="caption" color="error">At least one option is required for select/multiselect</Typography>
                )}
              </Box>
            )}

            <Stack direction="row" spacing={2}>
              <FormControlLabel
                control={<Switch checked={form.required} onChange={e => setForm(f => ({ ...f, required: e.target.checked }))} />}
                label="Required"
              />
              <TextField
                label="Sort Order"
                type="number"
                size="small"
                value={form.sortOrder}
                onChange={e => setForm(f => ({ ...f, sortOrder: parseInt(e.target.value) || 0 }))}
                sx={{ width: 120 }}
              />
            </Stack>

            <Divider />
            <Typography variant="subtitle2" fontWeight={600}>Budget</Typography>
            <Typography variant="caption" color="text.secondary" mt={-1.5}>
              The Service Wizard asks only what fits the budget the family gave this service. Leave blank for no limit.
            </Typography>
            <Stack direction="row" spacing={2}>
              <TextField label="Ask from budget (₹)" type="number" size="small" fullWidth value={form.minBudget}
                onChange={e => setForm(f => ({ ...f, minBudget: e.target.value }))} inputProps={{ min: 0 }} />
              <TextField label="Ask up to budget (₹)" type="number" size="small" fullWidth value={form.maxBudget}
                onChange={e => setForm(f => ({ ...f, maxBudget: e.target.value }))} inputProps={{ min: 0 }} />
            </Stack>
            {isChoice(form.type) && (
              <TextField select size="small" label="Is this the budget question?" value={form.budgetAnswer}
                onChange={e => setForm(f => ({ ...f, budgetAnswer: e.target.value }))}
                helperText="When yes, the option matching the budget is chosen for the family and the question is not asked">
                {BUDGET_ANSWER_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </TextField>
            )}
            {isChoice(form.type) && !form.budgetAnswer && form.options.length > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                  Offer an option only from a minimum service budget (₹), e.g. Palace/Heritage from 500000:
                </Typography>
                <Stack spacing={1}>
                  {form.options.map(opt => (
                    <Stack key={opt} direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap title={opt}>{opt}</Typography>
                      <TextField size="small" type="number" placeholder="Any budget" sx={{ width: 160 }} inputProps={{ min: 0 }}
                        value={form.optionBudgets[opt] ?? ''}
                        onChange={e => setForm(f => ({ ...f, optionBudgets: { ...f.optionBudgets, [opt]: e.target.value } }))} />
                    </Stack>
                  ))}
                </Stack>
              </Box>
            )}

            <Divider />
            <Typography variant="caption" color="text.secondary">Advanced: Conditional display</Typography>
            <Stack direction="row" spacing={2}>
              <TextField
                label="Depends On (question key)"
                size="small"
                value={form.dependsOn}
                onChange={e => setForm(f => ({ ...f, dependsOn: e.target.value }))}
                fullWidth
              />
              <TextField
                label="Show when value is"
                size="small"
                value={form.dependsOnValue}
                onChange={e => setForm(f => ({ ...f, dependsOnValue: e.target.value }))}
                fullWidth
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={creating}>
            {editTarget ? 'Save Changes' : 'Create Question'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack(s => ({ ...s, open: false }))}>
        <Alert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))}>{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
}
